// The audio engine (D-02): original music and sound, synthesised live with the Web Audio API (no audio files).
// Port of look-demo audio.ts: bus graph, band instruments and scheduler; the scores and Transport live in score.ts and titleScore.ts,
// the effects in sfx.ts. Volumes come from settings via setVolumes (music 0 = muted).
// Title screen (2026-10-05): one engine (and one AudioContext) for the whole page (audio/music.ts), with two songs on the same band,
// each behind its own faders: the title theme and the town music. play() crossfades between them (audio/mixer.ts).
import { tuning } from '@whiskerton/content';
import { MusicMixer, musicLevel, SONGS, type Fade, type Song } from './mixer';
import { BASS_FULL, BASS_HALF, EP_FULL, EP_HALF, hz, MOODS, sectionAt, TOWN_SCORE, Transport, type Lead, type Mood, type Score } from './score';
import { noiseBurst, osc, playSfx, type SfxName } from './sfx';
import { TITLE_MOODS, TITLE_SCORE } from './titleScore';

const LOOKAHEAD = 0.25, TICK_MS = 25;
/** Seconds after now that a song starting from the top plays its first step. */
const START_DELAY = 0.12;
type Buses = Record<'ep' | 'lead' | 'bass' | 'drums', GainNode>;
/** One song on the band: its score and moods, its clock while it plays, and its faders (dry to the master, sends to the reverb). */
interface Track { score: Score; moods: readonly Mood[]; mood: number; transport: Transport | null; buses: Buses; faders: GainNode[] }

export class Sound {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private tracks: Record<Song, Track> | null = null;
  private noise!: AudioBuffer;
  private analyser!: AnalyserNode;
  private mixer = new MusicMixer(tuning.title.crossfadeMs / 1000);
  /** The song asked for, kept from before the context exists (no gesture yet) until it does. */
  private wanted: Song | null = null;
  private townMood = 1;
  private timer: ReturnType<typeof setInterval> | null = null;
  private indoor = false;
  private music: number;
  private sfxLevel: number;
  private onVisibility: (() => void) | null = null;

  constructor(volumes: { music: number; sfx: number } = { music: 1, sfx: 1 }) {
    this.music = volumes.music;
    this.sfxLevel = volumes.sfx;
  }

  /** Browsers only allow audio after a gesture, so a tap (Play now, or the first tap in town) starts it. */
  unlock(): void {
    if (!this.ctx) this.build();
    const ctx = this.ctx!;
    if (ctx.state === 'suspended') void ctx.resume();
    if (this.timer === null) this.start();
  }
  /** Which song plays: crossfades to `song` (null fades the music out). Asked before the first gesture, it starts with the unlock. */
  play(song: Song | null): void {
    this.wanted = song;
    if (this.ctx && this.timer !== null) this.applySong();
  }
  /** For the test hooks: the context's state ('none' before the first gesture) and the song asked for. */
  status(): { context: AudioContextState | 'none'; song: Song | null } {
    return { context: this.ctx?.state ?? 'none', song: this.wanted };
  }

  /** The town music's mood (audio/mood.ts), landing on its next bar. */
  setMood(i: number): void {
    this.townMood = i;
    if (!this.tracks) return;
    this.tracks.town.mood = i;
    this.tracks.town.transport?.setMood(i);
  }
  setIndoor(v: boolean): void {
    this.indoor = v;
    this.applyLevel();
  }
  setVolumes(v: { music?: number; sfx?: number }): void {
    if (v.music !== undefined) this.music = v.music;
    if (v.sfx !== undefined) this.sfxLevel = v.sfx;
    this.applyLevel();
    if (this.ctx) this.sfxBus.gain.setTargetAtTime(0.6 * this.sfxLevel, this.ctx.currentTime, 0.05);
  }
  /** [rms, peak] of the output, for a dev meter. */
  meter(): [number, number] {
    if (!this.ctx) return [0, 0];
    const d = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(d);
    return [Math.sqrt(d.reduce((a, v) => a + v * v, 0) / d.length), d.reduce((a, v) => Math.max(a, Math.abs(v)), 0)];
  }

  sfx(name: SfxName): void {
    if (!this.ctx || this.ctx.state !== 'running' || this.sfxLevel <= 0) return;
    playSfx(this.ctx, this.sfxBus, this.noise, name);
  }

  /** A full teardown: stops the scheduler, closes the AudioContext and forgets the crossfade, so the next unlock() starts afresh. Nothing
   *  in the app calls it today (the page's one engine lives as long as the page, audio/music.ts pageSound); it is kept for a full teardown
   *  (tests, and a host shell that ends the page's audio) and sound.test.ts covers it. */
  dispose(): void {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    if (this.onVisibility && typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    this.onVisibility = null;
    if (this.ctx) void this.ctx.close();
    this.ctx = null;
    this.tracks = null;
    this.mixer = new MusicMixer(tuning.title.crossfadeMs / 1000);   // its fades were timed on the closed context's clock
  }

  private level(): number { return musicLevel(this.music, this.indoor); }
  private applyLevel(): void {
    if (this.ctx && this.timer !== null) this.master.gain.setTargetAtTime(this.level(), this.ctx.currentTime, 0.4);
  }

  private build(): void {
    const ctx = new AudioContext();
    this.ctx = ctx;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 9000;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.ratio.value = 3;
    comp.attack.value = 0.01;
    comp.release.value = 0.25;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    // A fast, hard limiter last in the chain, so nothing can ever clip.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -4;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    this.master.connect(comp).connect(lowpass).connect(limiter).connect(this.analyser).connect(ctx.destination);
    // Pause with the tab; browsers throttle timers in hidden tabs anyway.
    if (typeof document !== 'undefined') {
      this.onVisibility = () => {
        if (document.hidden) void ctx.suspend();
        else void ctx.resume();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = 0.6 * this.sfxLevel;
    this.sfxBus.connect(comp);

    // A warm room reverb from shaped noise.
    const len = Math.floor(ctx.sampleRate * 2.4);
    const ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      let smooth = 0;
      for (let i = 0; i < len; i++) {
        smooth = smooth * 0.6 + (Math.random() * 2 - 1) * 0.4;
        data[i] = smooth * Math.pow(1 - i / len, 2.6) * (i < ctx.sampleRate * 0.012 ? i / (ctx.sampleRate * 0.012) : 1);
      }
    }
    const reverb = ctx.createConvolver();
    reverb.buffer = ir;
    const wet = ctx.createGain();
    wet.gain.value = 0.9;
    reverb.connect(wet).connect(this.master);
    // Each song's band: the four buses, dry into the song's dry fader and sent into its reverb fader. Both faders start closed.
    const track = (score: Score, moods: readonly Mood[], mood: number): Track => {
      const dry = ctx.createGain(), send = ctx.createGain();
      dry.gain.value = 0;
      send.gain.value = 0;
      dry.connect(this.master);
      send.connect(reverb);
      const bus = (level: number, sendLevel: number) => {
        const g = ctx.createGain();
        g.gain.value = level;
        g.connect(dry);
        const s = ctx.createGain();
        s.gain.value = sendLevel;
        g.connect(s).connect(send);
        return g;
      };
      return { score, moods, mood, transport: null, faders: [dry, send],
        buses: { ep: bus(1, 0.32), lead: bus(1, 0.38), bass: bus(1, 0.06), drums: bus(1, 0.12) } };
    };
    this.tracks = { title: track(TITLE_SCORE, TITLE_MOODS, 0), town: track(TOWN_SCORE, MOODS, this.townMood) };
    const sfxSend = ctx.createGain();
    sfxSend.gain.value = 0.18;
    this.sfxBus.connect(sfxSend).connect(reverb);

    this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const n = this.noise.getChannelData(0);
    for (let i = 0; i < n.length; i++) n[i] = Math.random() * 2 - 1;
  }

  private start(): void {
    const ctx = this.ctx!;
    this.master.gain.cancelScheduledValues(ctx.currentTime);
    this.master.gain.setTargetAtTime(this.level(), ctx.currentTime, 0.6);
    this.timer = setInterval(() => this.schedule(), TICK_MS);
    this.applySong();
    this.schedule();
  }
  /** Moves the faders toward the wanted song; a song coming up from silence starts from the top, one still fading out carries on. */
  private applySong(): void {
    const tracks = this.tracks!, now = this.ctx!.currentTime, song = this.wanted;
    const restart = song !== null && this.mixer.restarts(song, now);
    for (const f of this.mixer.play(song, now)) this.fade(tracks[f.song], f);
    if (song && restart) tracks[song].transport = new Transport(now + START_DELAY, tracks[song].mood, tracks[song].moods);
  }
  private fade(t: Track, f: Fade): void {
    for (const g of t.faders) {
      g.gain.cancelScheduledValues(f.start);
      g.gain.setValueAtTime(f.from, f.start);
      g.gain.linearRampToValueAtTime(f.to, f.end);
    }
  }

  private schedule(): void {
    const ctx = this.ctx, tracks = this.tracks;
    if (!ctx || !tracks) return;
    const now = ctx.currentTime, active = this.mixer.active(now);
    for (const song of SONGS) {
      const t = tracks[song];
      if (!active.includes(song)) { t.transport = null; continue; }   // faded out: its clock stops
      // Muted music keeps the clock moving (so unmuting lands in time) but builds no nodes.
      t.transport?.advance(now, LOOKAHEAD, (beat, time, mood) => { if (this.music > 0) this.step(t, beat, time, mood); });
    }
  }

  // One eighth-note step of a song.
  private step(track: Track, beat: number, time: number, mood: Mood): void {
    const score = track.score, b = track.buses;
    const p = beat % score.length, spb = 60 / mood.bpm;
    const { notes, lead } = sectionAt(score, beat, mood);
    const t = time + (p % 1 === 0.5 ? 0.07 * spb : 0);
    const human = () => (Math.random() - 0.5) * 0.012;

    for (const c of score.chords) {
      for (const [off, d, v] of c.len === 4 ? EP_FULL : EP_HALF) {
        if (p !== c.at + off) continue;
        c.voicing.forEach((m, i) => this.ep(b, t + human() + i * 0.006, m, d * spb, v * mood.ep * (0.9 + Math.random() * 0.2)));
      }
      for (const [off, d, interval] of c.len === 4 ? BASS_FULL : BASS_HALF) {
        if (p === c.at + off) this.bass(b, t + human(), c.root + interval, d * spb, mood.bass);
      }
      if (!notes && p === c.at && Math.random() < 0.4) {               // a section of fills: chord tones up high
        const m = c.voicing[1 + Math.floor(Math.random() * 3)]! + 12 + mood.shift;
        this.lead(b, lead, t + human(), m, 2 * spb, 0.45);
      }
    }
    if (notes) {
      for (const [at, len, m] of notes) {
        if (at === p) this.lead(b, lead, t + human(), m + mood.shift, len * spb, 0.85 + Math.random() * 0.15);
      }
    }
    if (mood.shaker) this.shaker(b, t + human(), mood.shaker * (p % 1 === 0.5 ? 0.9 : 0.5) * (0.85 + Math.random() * 0.3));
    if (mood.rim && (p % 4 === 1 || p % 4 === 3)) this.rim(b, t + human(), mood.rim);
    if (mood.kick && (p % 4 === 0 || p % 4 === 2.5)) this.kick(b, t, mood.kick * (p % 4 === 0 ? 1 : 0.6));
  }

  // Instruments. Each note builds its own short-lived nodes, into the playing song's buses.
  private env(g: AudioParam, t: number, peak: number, attack: number, hold: number, release: number): void {
    g.setValueAtTime(0, t);
    g.linearRampToValueAtTime(peak, t + attack);
    g.setTargetAtTime(0, t + attack + hold, release);
  }

  // Electric piano: two-operator FM with a quick bright "tine" that settles into a warm tone.
  private ep(b: Buses, t: number, m: number, d: number, v: number): void {
    const ctx = this.ctx!, f = hz(m), end = t + d + 1.6;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(0.075 * v, t + 0.006);
    amp.gain.setTargetAtTime(0.035 * v, t + 0.006, 0.45);
    amp.gain.setTargetAtTime(0, t + d, 0.22);
    amp.connect(b.ep);
    const car = osc(ctx, 'sine', f, t, end, amp);
    const modGain = ctx.createGain();
    modGain.gain.setValueAtTime(f * 1.8 * v, t);
    modGain.gain.exponentialRampToValueAtTime(f * 0.18 + 1, t + 0.9);
    osc(ctx, 'sine', f, t, end, modGain);
    modGain.connect(car.frequency);
    const tine = ctx.createGain();
    tine.gain.setValueAtTime(f * 0.9 * v, t);
    tine.gain.exponentialRampToValueAtTime(1, t + 0.08);
    osc(ctx, 'sine', f * 14, t, t + 0.12, tine);
    tine.connect(car.frequency);
  }

  private bass(b: Buses, t: number, m: number, d: number, v: number): void {
    const ctx = this.ctx!, f = hz(m), end = t + d + 0.5;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 680;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(0.3 * v, t + 0.012);
    amp.gain.setTargetAtTime(0.17 * v, t + 0.012, 0.22);
    amp.gain.setTargetAtTime(0, t + d, 0.06);
    lp.connect(amp).connect(b.bass);
    osc(ctx, 'triangle', f, t, end, lp);
    osc(ctx, 'sine', f, t, end, lp);
  }

  private lead(b: Buses, kind: Lead, t: number, m: number, d: number, v: number): void {
    if (kind === 'flute') return this.flute(b, t, m, d, v);
    // Mallets and bells: a few sine partials, each with its own decay.
    const partials: [number, number, number][] =
      kind === 'marimba' ? [[1, 1, 0.5], [3.93, 0.28, 0.1], [9.2, 0.06, 0.03]] :
      kind === 'glock' ? [[1, 1, 1.3], [2.76, 0.32, 0.45], [5.4, 0.12, 0.18]] :
      [[1, 1, 1.6], [2, 0.14, 0.9], [4.1, 0.22, 0.45], [6.3, 0.06, 0.2]];
    const peak = kind === 'marimba' ? 0.2 : kind === 'glock' ? 0.12 : 0.13;
    const ctx = this.ctx!, f = hz(m);
    for (const [ratio, amp, decay] of partials) {
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(peak * amp * v, t + 0.004);
      g.gain.setTargetAtTime(0, t + 0.004, decay * (kind === 'marimba' ? Math.min(1.4, 0.5 + d) : 1));
      g.connect(b.lead);
      osc(ctx, 'sine', f * ratio, t, t + decay * 6 + 0.1, g);
    }
    if (kind === 'marimba') this.noiseHit(t, 2600, 4, 0.025 * v, 0.012, b.lead);
  }

  private flute(b: Buses, t: number, m: number, d: number, v: number): void {
    const ctx = this.ctx!, f = hz(m), end = t + d + 0.6;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(0.13 * v, t + 0.07);
    amp.gain.setTargetAtTime(0.105 * v, t + 0.07, 0.3);
    amp.gain.setTargetAtTime(0, t + d * 0.92, 0.08);
    amp.connect(b.lead);
    const o = osc(ctx, 'sine', f, t, end, amp);
    const o2 = ctx.createGain();
    o2.gain.value = 0.18;
    o2.connect(amp);
    osc(ctx, 'triangle', f * 2, t, end, o2);
    const vib = ctx.createGain();
    vib.gain.setValueAtTime(0, t);
    vib.gain.linearRampToValueAtTime(f * 0.007, t + 0.35);
    vib.connect(o.frequency);
    osc(ctx, 'sine', 5.2, t, end, vib);
    this.noiseHit(t, f * 2, 1.2, 0.02 * v, d * 0.6, b.lead);
  }

  private noiseHit(t: number, freq: number, q: number, peak: number, decay: number, out: AudioNode): void {
    noiseBurst(this.ctx!, this.noise, t, freq, q, peak, decay, out);
  }
  private shaker(b: Buses, t: number, v: number): void { this.noiseHit(t, 7200, 0.9, 0.05 * v, 0.06, b.drums); }
  private rim(b: Buses, t: number, v: number): void {
    this.noiseHit(t, 2400, 7, 0.09 * v, 0.03, b.drums);
    const g = this.ctx!.createGain();
    this.env(g.gain, t, 0.05 * v, 0.002, 0.004, 0.012);
    g.connect(b.drums);
    osc(this.ctx!, 'triangle', 1750, t, t + 0.08, g);
  }
  private kick(b: Buses, t: number, v: number): void {
    const g = this.ctx!.createGain();
    this.env(g.gain, t, 0.32 * v, 0.004, 0.02, 0.09);
    g.connect(b.drums);
    const o = osc(this.ctx!, 'sine', 110, t, t + 0.45, g);
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(46, t + 0.12);
  }
}
